import { Head, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import type DestinationsIndex from "@travily/types/pages/DestinationsIndex";
import { SearchBar } from "@ui/components/SearchBar";
import { useState } from "react";
import { Configure } from "react-instantsearch";
import { SearchRoot } from "@/components/Search/SearchRoot";
import { useSearchBarWhere } from "@/hooks/use-search-bar-where";
import { useFavoritedIdsCallbacks } from "@/hooks/use-search-favorites";
import { useSearchSessionUiState } from "@/hooks/use-search-session";
import { ExploreFiltersProvider } from "@/layouts/components/explore-filters-context";
import { DESTINATION_WHERE_TYPES } from "@/layouts/components/explore-sections";
import { PageFrame } from "@/layouts/page-frame";
import { parseWhenParams, parseWhereParams } from "@/lib/search-params";
import {
  DestinationsFiltersBody,
  DestinationsFiltersFooterAdapter,
  DestinationsFiltersRefinements,
  useDestinationFilters,
} from "./DestinationsFiltersAdapter";
import { DestinationsSearchAdapter } from "./DestinationsSearchAdapter";
import { DestinationsSortProvider, useDestinationsSort } from "./DestinationsSortContext";
import { DestinationsUrlSync } from "./DestinationsUrlSync";
import { IndexView } from "./index.view";
import { hasDestinationsSearchParams, parseDestinationsFilterParams, toDestinationsUiState } from "./search-params";
import { useDestinationsSearch } from "./useDestinationsSearch";

/** Renders the SearchBar sort pill from the shared sort context. Lives inside the SearchBar
 *  (under `DestinationsSortProvider`), next to the Filters button. */
function DestinationsSortControl() {
  const sort = useDestinationsSort();
  return <SearchBar.Sort currentValue={sort.currentValue} onChange={sort.refine} options={sort.options} />;
}

// `configure` carries the scoring signature below; it's derived from local state
// and re-applied on mount, so persisting it would risk restoring a stale window.
// `range` (the budget) is local state too — restoring it would double-apply
// against the BudgetRangeRefinement bridge.
const SESSION_OMIT = ["refinementList", "range", "configure"];

// The When window and the budget drive the backend scorer via the request
// enhancer (the Searchkit adapter won't forward a custom param into the ES
// body). But the adapter/InstantSearch result cache is keyed on the
// InstantSearch request, which is otherwise identical across them — so without
// surfacing them here, InstantSearch serves stale hits for a changed window or
// budget. Registering them as a search parameter via <Configure> makes both
// part of that cache key: a new window or budget is a new search that renders
// fresh results. The value never reaches ES (the adapter drops unknown params);
// it exists purely to key the search.
type ConfigureProps = React.ComponentProps<typeof Configure>;

function ScoringConfigure({ budget, effectiveMonths }: { budget: number | null; effectiveMonths: number[] }) {
  // `as` bridges a types-vs-runtime gap: <Configure> forwards arbitrary search
  // parameters at runtime, but its prop type only models known Algolia keys.
  const props = { travilyScoring: `${effectiveMonths.join(",")}:${budget ?? ""}` } as ConfigureProps;
  return <Configure {...props} />;
}

const DestinationsIndexPage = ({
  favorited_ids,
  ranking_tiers,
  ranking_version,
  seed_results,
  seed_total,
}: DestinationsIndex) => {
  // Parsed once per mount: shared links, back/forward (Inertia remounts on
  // popstate), and the mobile overlay hand-off all rehydrate through it.
  const pageUrl = usePage().url;
  const [initialSearch] = useState(() => ({
    where: parseWhereParams(pageUrl) ?? undefined,
    when: parseWhenParams(pageUrl) ?? undefined,
    filterParams: parseDestinationsFilterParams(pageUrl),
  }));
  const filters = useDestinationFilters({
    initialWhen: initialSearch.when,
    initialBands: initialSearch.filterParams.selectedBands,
    initialBudget: initialSearch.filterParams.budget,
    initialKoppen: initialSearch.filterParams.selectedKoppen,
    initialLanguages: initialSearch.filterParams.selectedLanguages,
    initialOffsets: initialSearch.filterParams.selectedOffsets,
  });
  const search = useDestinationsSearch({
    budget: filters.budgetCeiling,
    effectiveMonths: filters.effectiveMonths,
    selectedBands: filters.selectedBands,
  });
  const { getFavorited, onFavoriteToggle } = useFavoritedIdsCallbacks(favorited_ids);
  // A URL carrying any owned search param wins over the restored session;
  // DestinationsUrlSync keeps the URL current from then on. refinementList is
  // omitted: its widgets mount off per-visit local filter state.
  const { initialUiState, onStateChange } = useSearchSessionUiState("destinations", {
    urlUiState: toDestinationsUiState(pageUrl, search.indexName),
    hasUrlOverride: hasDestinationsSearchParams(pageUrl),
    omit: SESSION_OMIT,
  });
  const { whereProps, whereBoundsBox, whereBbox, whereFilter, markMapArea } = useSearchBarWhere({
    types: DESTINATION_WHERE_TYPES,
    termFilters: true,
    ...(initialSearch.where ? { initial: initialSearch.where } : {}),
  });
  // Seed only the untouched default page: a shared link carrying search params
  // would flash the default grid before its own results land.
  const [seedDestinations] = useState(() => (hasDestinationsSearchParams(pageUrl) ? [] : seed_results));
  const [seedTotal] = useState(() => (hasDestinationsSearchParams(pageUrl) ? null : seed_total));

  // SearchBar lives inside the TopBar's `bottomContent` slot so it persists across
  // scrolls and the map's full-viewport calc adapts via --frame-topbar-h. Hidden at
  // narrow viewports — discovery chrome is desktop-only on /destinations today.
  const renderFiltersFooter = (close: () => void) => (
    <DestinationsFiltersFooterAdapter clearFilters={filters.clearFilters} onApply={close} />
  );

  const searchBar = (
    <IndexView
      filters={<DestinationsFiltersBody {...filters} />}
      onWhenChange={filters.setWhenSelection}
      renderFiltersFooter={renderFiltersFooter}
      sortControl={<DestinationsSortControl />}
      whenValue={filters.whenSelection}
      whereProps={whereProps}
    />
  );

  return (
    <SearchRoot initialUiState={initialUiState} onStateChange={onStateChange} search={search}>
      <ExploreFiltersProvider filters={<DestinationsFiltersBody {...filters} />} footer={renderFiltersFooter} size="lg">
        <DestinationsSortProvider>
          <PageFrame
            contentMaxWidth="full"
            contentPadded={false}
            footerFullBleed
            mobileTopBarShadow="always"
            topBarBottomContent={searchBar}
          >
            <Head title={t("web.destinations.index.meta_title")}>
              <meta name="description" content={t("web.destinations.index.meta_description")} />
            </Head>
            <DestinationsUrlSync
              filters={filters}
              whereBbox={whereBbox}
              whereFilter={whereFilter}
              whereValue={whereProps.value}
            />
            <DestinationsFiltersRefinements {...filters} />
            <ScoringConfigure budget={filters.budgetCeiling} effectiveMonths={filters.effectiveMonths} />
            <DestinationsSearchAdapter
              filters={filters}
              getFavorited={getFavorited}
              markMapArea={markMapArea}
              onFavoriteToggle={onFavoriteToggle}
              rankingTiers={ranking_tiers}
              rankingVersion={ranking_version}
              scoring={search.scoring}
              seedDestinations={seedDestinations}
              seedTotal={seedTotal}
              whereBbox={whereBbox}
              whereBoundsBox={whereBoundsBox}
              whereFilter={whereFilter}
              whereValue={whereProps.value}
            />
          </PageFrame>
        </DestinationsSortProvider>
      </ExploreFiltersProvider>
    </SearchRoot>
  );
};

export default DestinationsIndexPage;
